import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  DatePicker,
  InputNumber,
  Select,
  Switch,
  Card,
  Divider,
  Space,
  Tag,
  Checkbox,
  Typography,
  Alert,
  Tooltip,
  Badge,
  Spin,
  Slider,
  Popover,
  Dropdown,
  Menu,
  Modal,
  ColorPicker,
} from "antd";
import {
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  SettingOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  BgColorsOutlined,
  GlobalOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  WarningOutlined,
  ReloadOutlined,
  DownOutlined,
  FileAddOutlined,
  HolderOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useParams, withRouter } from "react-router";
import { getPromotionById, updatePromotionById } from "@api/subscription/promotion";
import { SuccessNotification, ErrorNotification } from "@components";
import moment from "moment";

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const promotionTypes = [
  { value: "banner", label: "Banner", icon: "🎯", description: "Top or bottom banner" },
  { value: "popup", label: "Popup", icon: "💬", description: "Modal popup" },
  { value: "floating", label: "Floating", icon: "🎈", description: "Floating widget" },
  { value: "inline", label: "Inline", icon: "📝", description: "Inline content" },
  { value: "exit_intent", label: "Exit Intent", icon: "👋", description: "Exit intent popup" },
  { value: "gamified", label: "Gamified", icon: "🎮", description: "Interactive promotion" },
];

const displayStyles = [
  { value: "minimal", label: "Minimal", description: "Clean and simple" },
  { value: "standard", label: "Standard", description: "Balanced design" },
  { value: "bold", label: "Bold", description: "Eye-catching" },
  { value: "festive", label: "Festive", description: "Celebration themed" },
  { value: "gradient", label: "Gradient", description: "Modern gradient look" },
];

const targetAudiences = [
  { value: "all", label: "All Users", icon: "👥" },
  { value: "free", label: "Free Users", icon: "🆓" },
  { value: "trial", label: "Trial Users", icon: "⏱️" },
  { value: "paid", label: "Paid Users", icon: "💎" },
  { value: "churned", label: "Churned Users", icon: "💔" },
  { value: "new", label: "New Users", icon: "✨" },
  { value: "returning", label: "Returning Users", icon: "🔄" },
];

const themeOptions = [
  { value: "default", label: "Default", color: "#8B5CF6" },
  { value: "dark", label: "Dark", color: "#1f2937" },
  { value: "light", label: "Light", color: "#f3f4f6" },
  { value: "gradient_purple", label: "Purple Gradient", color: "#8B5CF6" },
  { value: "gradient_blue", label: "Blue Gradient", color: "#3B82F6" },
  { value: "gradient_green", label: "Green Gradient", color: "#10B981" },
  { value: "gradient_yellow", label: "Yellow Gradient", color: "#F59E0B" },
  { value: "gradient_orange", label: "Orange Gradient", color: "#F97316" },
  { value: "gradient_pink", label: "Pink Gradient", color: "#EC4899" },
  { value: "gradient_red", label: "Red Gradient", color: "#EF4444" },
  { value: "festive", label: "Festive", color: "#EF4444" },
  { value: "holiday", label: "Holiday", color: "#10B981" },
  { value: "minimal", label: "Minimal", color: "#6B7280" },
  { value: "bold", label: "Bold", color: "#1F2937" },
  { value: "elegant", label: "Elegant", color: "#7C3AED" },
  { value: "playful", label: "Playful", color: "#06B6D4" },
];

const animationOptions = [
  { value: "none", label: "None" },
  { value: "fade", label: "Fade In" },
  { value: "slide", label: "Slide In" },
  { value: "bounce", label: "Bounce" },
  { value: "pulse", label: "Pulse" },
  { value: "shake", label: "Shake" },
];

// Extended gamification types with all options
const gamificationTypes = [
  { value: "none", label: "None", icon: "➖", description: "No gamification" },
  { value: "spin_wheel", label: "Spin Wheel", icon: "🎡", description: "Wheel of fortune style game" },
  { value: "scratch_card", label: "Scratch Card", icon: "🎴", description: "Scratch to reveal prizes" },
  { value: "slot_machine", label: "Slot Machine", icon: "🎰", description: "Slot machine with symbols" },
  { value: "countdown", label: "Countdown Timer", icon: "⏰", description: "Urgency countdown timer" },
  { value: "progress_bar", label: "Progress Bar", icon: "📊", description: "Step-based progress tracker" },
  { value: "streak", label: "Daily Streak", icon: "🔥", description: "Daily streak rewards" },
];

// Preset slot machine symbols
const slotSymbols = [
  { emoji: "🍒", label: "Cherry" },
  { emoji: "🍋", label: "Lemon" },
  { emoji: "🍊", label: "Orange" },
  { emoji: "🍇", label: "Grape" },
  { emoji: "🍉", label: "Watermelon" },
  { emoji: "7️⃣", label: "Seven" },
  { emoji: "💎", label: "Diamond" },
  { emoji: "⭐", label: "Star" },
  { emoji: "🔔", label: "Bell" },
  { emoji: "💰", label: "Money Bag" },
  { emoji: "🎁", label: "Gift" },
  { emoji: "🏆", label: "Trophy" },
  { emoji: "💵", label: "Dollar" },
  { emoji: "🎯", label: "Target" },
  { emoji: "🚀", label: "Rocket" },
];

// Default segment colors
const segmentColors = [
  "#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#EC4899",
  "#6366F1", "#14B8A6", "#F97316", "#84CC16", "#06B6D4", "#A855F7",
];

const showOnPagesOptions = [
  { value: "popup", label: "Popup" },
  { value: "widget", label: "Widget" },
  { value: "settings", label: "Settings" },
  { value: "all", label: "All Pages" },
];

const countdownPositions = [
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom" },
  { value: "inline", label: "Inline with content" },
  { value: "floating", label: "Floating" },
];

// DEFAULT PRESET VALUES for each gamification type
const gamificationDefaults = {
  spin_wheel: {
    wheelSegments: [
      { label: "10% Off", value: 10, probability: 30, color: "#8B5CF6" },
      { label: "20% Off", value: 20, probability: 25, color: "#3B82F6" },
      { label: "30% Off", value: 30, probability: 15, color: "#10B981" },
      { label: "50% Off", value: 50, probability: 5, color: "#F59E0B" },
      { label: "Try Again", value: 0, probability: 25, color: "#EF4444" },
    ],
  },
  scratch_card: {
    scratchThreshold: 50,
    prizeDisplayText: "Congratulations! You won:",
  },
  slot_machine: {
    slotSymbols: ["7", "🍒", "💎", "🔔", "⭐"],
    winningSymbol: "7",
    spinDuration: 3,
  },
  countdown: {
    countdownPosition: "top",
    urgencyMessage: "Hurry! Offer ends soon!",
  },
  progress_bar: {
    progressSteps: 5,
    currentStep: 1,
    stepLabels: ["Sign Up", "Explore", "Engage", "Subscribe", "Complete"],
  },
  streak: {
    streakDays: 7,
    streakRewards: [
      { day: 1, label: "Day 1 Bonus", value: "5% Off" },
      { day: 3, label: "Day 3 Bonus", value: "10% Off" },
      { day: 5, label: "Day 5 Bonus", value: "15% Off" },
      { day: 7, label: "Week Streak!", value: "25% Off" },
    ],
  },
  quiz: {
    questions: [],
    passingScore: 70,
  },
};

// Template Presets for quick setup
const gamificationTemplates = [
  {
    id: "holiday_spin_wheel",
    name: "Holiday Spin Wheel",
    icon: "🎄",
    description: "Perfect for seasonal holiday promotions",
    gamificationType: "spin_wheel",
    config: {
      wheelSegments: [
        { label: "Free Shipping", value: 0, probability: 20, color: "#10B981" },
        { label: "15% Off", value: 15, probability: 25, color: "#EF4444" },
        { label: "25% Off", value: 25, probability: 20, color: "#10B981" },
        { label: "Holiday Gift", value: 0, probability: 10, color: "#F59E0B" },
        { label: "50% Off", value: 50, probability: 5, color: "#8B5CF6" },
        { label: "Try Again", value: 0, probability: 20, color: "#6B7280" },
      ],
    },
    styling: {
      theme: "holiday",
      primaryColor: "#DC2626",
      secondaryColor: "#10B981",
    },
    content: {
      headline: "Spin to Win Holiday Deals!",
      subheadline: "Spin the wheel for exclusive holiday savings!",
      ctaText: "Spin Now",
      badge: "HOLIDAY SPECIAL",
    },
  },
  {
    id: "flash_sale_countdown",
    name: "Flash Sale Countdown",
    icon: "⚡",
    description: "Create urgency with a ticking countdown",
    gamificationType: "countdown",
    config: {
      countdownPosition: "top",
      urgencyMessage: "Flash Sale Ends In:",
    },
    styling: {
      theme: "dark",
      primaryColor: "#EF4444",
      secondaryColor: "#F59E0B",
    },
    content: {
      headline: "Flash Sale - Up to 70% Off!",
      subheadline: "Don't miss out on these incredible deals!",
      ctaText: "Shop Now",
      badge: "FLASH SALE",
    },
  },
  {
    id: "loyalty_streak",
    name: "Loyalty Streak",
    icon: "🔥",
    description: "Reward daily engagement with streak bonuses",
    gamificationType: "streak",
    config: {
      streakDays: 7,
      streakRewards: [
        { day: 1, label: "Welcome Bonus", value: "5% Off" },
        { day: 2, label: "Day 2 Reward", value: "Free Trial Extension" },
        { day: 3, label: "Midweek Bonus", value: "10% Off" },
        { day: 5, label: "Almost There!", value: "15% Off" },
        { day: 7, label: "Loyalty Champion", value: "30% Off + Free Month" },
      ],
    },
    styling: {
      theme: "gradient_purple",
      primaryColor: "#F97316",
      secondaryColor: "#EF4444",
    },
    content: {
      headline: "Build Your Streak, Unlock Rewards!",
      subheadline: "Check in daily to unlock exclusive bonuses",
      ctaText: "Start Streak",
      badge: "LOYALTY PROGRAM",
    },
  },
  {
    id: "mystery_scratch_card",
    name: "Mystery Scratch Card",
    icon: "🎴",
    description: "Exciting scratch-to-reveal experience",
    gamificationType: "scratch_card",
    config: {
      scratchThreshold: 60,
      prizeDisplayText: "You've uncovered a mystery prize!",
    },
    styling: {
      theme: "gradient_blue",
      primaryColor: "#6366F1",
      secondaryColor: "#8B5CF6",
    },
    content: {
      headline: "Scratch to Reveal Your Prize!",
      subheadline: "A mystery discount awaits you!",
      ctaText: "Start Scratching",
      badge: "MYSTERY DEAL",
    },
  },
  {
    id: "jackpot_slots",
    name: "Jackpot Slots",
    icon: "🎰",
    description: "Vegas-style slot machine excitement",
    gamificationType: "slot_machine",
    config: {
      slotSymbols: ["7", "🍒", "💎", "🔔", "💰", "⭐"],
      winningSymbol: "7",
      spinDuration: 4,
    },
    styling: {
      theme: "dark",
      primaryColor: "#F59E0B",
      secondaryColor: "#EF4444",
    },
    content: {
      headline: "Hit the Jackpot!",
      subheadline: "Spin the reels for a chance to win big discounts!",
      ctaText: "Pull the Lever",
      badge: "JACKPOT GAME",
    },
  },
];

// Form validation rules
const validationRules = {
  name: {
    required: true,
    message: "Please enter a promotion name",
  },
  schedule: {
    required: true,
    message: "Please select start and end dates in the Schedule section",
  },
  headline: {
    required: true,
    message: "Please enter a headline in the Content section",
  },
  wheelProbability: {
    validate: (segments) => segments.reduce((sum, seg) => sum + (seg.probability || 0), 0) === 100,
    message: "Wheel segment probabilities must sum to exactly 100%",
  },
  wheelSegments: {
    minCount: 2,
    message: "Spin wheel must have at least 2 segments",
  },
  slotSymbols: {
    minCount: 3,
    message: "Slot machine must have at least 3 symbols",
  },
  streakDays: {
    min: 1,
    max: 30,
    message: "Streak days must be between 1 and 30",
  },
};

const initialState = {
  name: "",
  slug: "",
  type: "banner",
  displayStyle: "standard",
  isActive: true,
  priority: 5,
  targetAudience: ["all"],
  excludedUserIds: [],
  includedUserIds: [],
  schedule: {
    startDate: null,
    endDate: null,
  },
  discount: {
    discountType: "percentage",
    discountValue: 0,
    currency: "USD",
    applicablePlans: [],
  },
  coupon: {
    code: "",
    autoApply: false,
  },
  styling: {
    theme: "default",
    primaryColor: "#8B5CF6",
    secondaryColor: "#06B6D4",
    animation: "fade",
  },
  displayConditions: {
    showOnPages: ["all"],
    maxImpressions: 0,
    maxImpressionsPerUser: 3,
    delaySeconds: 0,
    scrollPercentage: 0,
  },
  urgency: {
    showCountdown: false,
    stockLimit: 0,
    usageLimit: 0,
  },
  gamification: {
    type: "none",
    // Spin Wheel / Wheel config
    wheelSegments: [],
    // Scratch Card config
    scratchThreshold: 50,
    prizeDisplayText: "You won!",
    // Slot Machine config
    slotSymbols: ["🍒", "7️⃣", "💎"],
    winningSymbol: "7️⃣",
    spinDuration: 3,
    // Countdown config
    countdownPosition: "top",
    urgencyMessage: "Hurry! Offer ends soon!",
    // Progress Bar config
    progressSteps: 5,
    currentStep: 1,
    stepLabels: [],
    // Streak config
    streakDays: 7,
    streakRewards: [],
  },
  content: {
    en: {
      headline: "",
      subheadline: "",
      ctaText: "",
      ctaSecondaryText: "",
      badge: "",
      terms: "",
    },
  },
};

// Styles
const styles = {
  pageContainer: {
    maxWidth: 1400,
    margin: "0 auto",
    padding: "24px 24px 48px",
  },
  header: {
    marginBottom: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  infoBanner: {
    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    borderRadius: 16,
    padding: "24px 32px",
    marginBottom: 24,
    color: "#fff",
    position: "relative",
    overflow: "hidden",
  },
  infoBannerPattern: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: "40%",
    background: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
    opacity: 0.5,
  },
  mainCard: {
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
  },
  sectionNav: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: "20px 16px",
    borderRight: "1px solid #f0f0f0",
    background: "#fafafa",
    minHeight: 600,
  },
  navItem: (isActive) => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    borderRadius: 10,
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: isActive ? "#fff" : "transparent",
    border: isActive ? "1px solid #e5e7eb" : "1px solid transparent",
    boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
  }),
  navIcon: (isActive, color) => ({
    width: 36,
    height: 36,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: isActive ? `${color}15` : "#f3f4f6",
    color: isActive ? color : "#9ca3af",
    fontSize: 16,
  }),
  sectionContent: {
    padding: "24px 32px",
    minHeight: 600,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: "#111827",
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 14,
    color: "#6b7280",
    marginBottom: 24,
  },
  formGroup: {
    marginBottom: 24,
  },
  formLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
    fontWeight: 500,
    color: "#374151",
  },
  colorPickerWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 8,
    background: "#fff",
  },
  colorSwatch: (color) => ({
    width: 32,
    height: 32,
    borderRadius: 6,
    background: color,
    border: "2px solid #fff",
    boxShadow: "0 0 0 1px #d9d9d9",
    cursor: "pointer",
  }),
  previewCard: {
    position: "sticky",
    top: 24,
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  previewHeader: {
    padding: "16px 20px",
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  previewContent: {
    padding: 20,
    minHeight: 300,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  promotionPreview: (primaryColor, secondaryColor) => ({
    width: "100%",
    padding: 24,
    borderRadius: 12,
    background: `linear-gradient(135deg, ${primaryColor}15, ${secondaryColor}15)`,
    border: `1px solid ${primaryColor}30`,
    textAlign: "center",
  }),
  wheelSegmentRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    background: "#f9fafb",
    borderRadius: 10,
    marginBottom: 12,
  },
  streakRewardRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    background: "#f9fafb",
    borderRadius: 10,
    marginBottom: 12,
  },
  stepLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    background: "#f9fafb",
    borderRadius: 8,
    marginBottom: 8,
  },
  symbolGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 8,
    padding: 8,
  },
  symbolButton: (isSelected) => ({
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    borderRadius: 8,
    border: isSelected ? "2px solid #8B5CF6" : "1px solid #e5e7eb",
    background: isSelected ? "#8B5CF615" : "#fff",
    cursor: "pointer",
    transition: "all 0.2s ease",
  }),
  footer: {
    padding: "20px 32px",
    background: "#f9fafb",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "calc(80vh - 50px)",
  },
  probabilityWarning: {
    padding: "12px 16px",
    background: "#FEF3C7",
    borderRadius: 8,
    border: "1px solid #F59E0B",
    marginTop: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  configCard: {
    background: "#f9fafb",
    borderRadius: 12,
    padding: 20,
    marginTop: 16,
    border: "1px solid #e5e7eb",
  },
  configTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  templateCard: {
    padding: 16,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: "#fff",
  },
  templateCardHover: {
    borderColor: "#8B5CF6",
    boxShadow: "0 4px 12px rgba(139, 92, 246, 0.15)",
  },
  resetButton: {
    borderRadius: 6,
    height: 32,
  },
  distributionButton: {
    flex: 1,
    borderRadius: 6,
  },
  distributionButtonGroup: {
    display: "flex",
    gap: 8,
    marginBottom: 16,
  },
  dragHandle: {
    cursor: "move",
    color: "#9ca3af",
    padding: "0 8px",
  },
  segmentRowDraggable: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 16px",
    background: "#f9fafb",
    borderRadius: 10,
    marginBottom: 12,
    border: "1px solid transparent",
    transition: "all 0.2s ease",
  },
  segmentRowDragging: {
    border: "1px dashed #8B5CF6",
    background: "#f5f3ff",
  },
  colorPickerEnhanced: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  validationError: {
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "12px 16px",
    marginTop: 12,
  },
  validationSuccess: {
    background: "#F0FDF4",
    border: "1px solid #BBF7D0",
    borderRadius: 8,
    padding: "12px 16px",
    marginTop: 12,
  },
};

const sections = [
  { key: "basic", label: "Basic Info", icon: <SettingOutlined />, color: "#8B5CF6" },
  { key: "targeting", label: "Targeting", icon: <UserOutlined />, color: "#3B82F6" },
  { key: "schedule", label: "Schedule", icon: <CalendarOutlined />, color: "#10B981" },
  { key: "discount", label: "Discount", icon: <DollarOutlined />, color: "#F59E0B" },
  { key: "styling", label: "Styling", icon: <BgColorsOutlined />, color: "#EC4899" },
  { key: "display", label: "Display Rules", icon: <GlobalOutlined />, color: "#6366F1" },
  { key: "urgency", label: "Urgency", icon: <ThunderboltOutlined />, color: "#EF4444" },
  { key: "gamification", label: "Gamification", icon: <TrophyOutlined />, color: "#F97316" },
  { key: "content", label: "Content", icon: <GlobalOutlined />, color: "#14B8A6" },
];

const PromotionUpdateForm = ({ history }) => {
  const { id } = useParams();
  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");
  const [activeLocale, setActiveLocale] = useState("en");
  const [wheelSegments, setWheelSegments] = useState([]);
  const [streakRewards, setStreakRewards] = useState([]);
  const [stepLabels, setStepLabels] = useState([]);
  const [draggedSegmentIndex, setDraggedSegmentIndex] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [templateModalVisible, setTemplateModalVisible] = useState(false);

  // Calculate wheel probability sum
  const probabilitySum = useMemo(() => {
    return wheelSegments.reduce((sum, seg) => sum + (seg.probability || 0), 0);
  }, [wheelSegments]);

  // Validation helper functions
  const validateForm = useCallback(() => {
    const errors = {};
    const normalizedType = normalizeGamificationType(values.gamification?.type);

    // Basic validation
    if (!values.name?.trim()) {
      errors.name = validationRules.name.message;
    }

    if (!values.schedule?.startDate || !values.schedule?.endDate) {
      errors.schedule = validationRules.schedule.message;
    }

    if (!values.content?.en?.headline?.trim()) {
      errors.headline = validationRules.headline.message;
    }

    // Gamification-specific validation
    if (normalizedType === "spin_wheel") {
      if (wheelSegments.length < 2) {
        errors.wheelSegments = validationRules.wheelSegments.message;
      }
      if (probabilitySum !== 100) {
        errors.wheelProbability = `${validationRules.wheelProbability.message} (current: ${probabilitySum}%)`;
      }
      // Check for empty labels
      const emptyLabels = wheelSegments.filter(s => !s.label?.trim());
      if (emptyLabels.length > 0) {
        errors.wheelLabels = "All wheel segments must have labels";
      }
    }

    if (normalizedType === "slot_machine") {
      const symbols = values.gamification?.slotSymbols || [];
      if (symbols.length < 3) {
        errors.slotSymbols = validationRules.slotSymbols.message;
      }
    }

    if (normalizedType === "streak") {
      const days = values.gamification?.streakDays || 0;
      if (days < 1 || days > 30) {
        errors.streakDays = validationRules.streakDays.message;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [values, wheelSegments, probabilitySum]);

  // Reset gamification to defaults
  const resetGamificationToDefaults = useCallback((gamificationType) => {
    const normalizedType = normalizeGamificationType(gamificationType);
    const defaults = gamificationDefaults[normalizedType];

    if (!defaults) return;

    Modal.confirm({
      title: "Reset to Default Values",
      content: `Are you sure you want to reset ${gamificationTypes.find(t => normalizeGamificationType(t.value) === normalizedType)?.label || normalizedType} configuration to default values? This will overwrite your current settings.`,
      okText: "Reset",
      okType: "primary",
      cancelText: "Cancel",
      onOk() {
        if (normalizedType === "spin_wheel" && defaults.wheelSegments) {
          setWheelSegments([...defaults.wheelSegments]);
        } else if (normalizedType === "streak" && defaults.streakRewards) {
          setStreakRewards([...defaults.streakRewards]);
          handleGamificationChange("streakDays", defaults.streakDays);
        } else if (normalizedType === "progress_bar" && defaults.stepLabels) {
          setStepLabels([...defaults.stepLabels]);
          handleGamificationChange("progressSteps", defaults.progressSteps);
          handleGamificationChange("currentStep", defaults.currentStep);
        } else {
          // Apply other defaults directly to gamification state
          Object.entries(defaults).forEach(([key, value]) => {
            handleGamificationChange(key, value);
          });
        }
        SuccessNotification("Configuration reset to defaults");
      },
    });
  }, []);

  // Apply template
  const applyTemplate = useCallback((template) => {
    Modal.confirm({
      title: `Apply "${template.name}" Template`,
      content: "This will update your gamification settings, styling, and content. Your other promotion settings will be preserved. Continue?",
      okText: "Apply Template",
      okType: "primary",
      cancelText: "Cancel",
      onOk() {
        // Apply gamification type and config
        handleGamificationChange("type", template.gamificationType);

        if (template.config.wheelSegments) {
          setWheelSegments([...template.config.wheelSegments]);
        }
        if (template.config.streakRewards) {
          setStreakRewards([...template.config.streakRewards]);
        }
        Object.entries(template.config).forEach(([key, value]) => {
          if (key !== "wheelSegments" && key !== "streakRewards") {
            handleGamificationChange(key, value);
          }
        });

        // Apply styling
        if (template.styling) {
          setValues((prev) => ({
            ...prev,
            styling: { ...prev.styling, ...template.styling },
          }));
        }

        // Apply content
        if (template.content) {
          setValues((prev) => ({
            ...prev,
            content: {
              ...prev.content,
              en: { ...prev.content?.en, ...template.content },
            },
          }));
        }

        setTemplateModalVisible(false);
        SuccessNotification(`Template "${template.name}" applied successfully!`);
      },
    });
  }, []);

  // Probability distribution helpers for spin wheel
  const distributeEqualProbability = useCallback(() => {
    if (wheelSegments.length === 0) return;
    const equalProb = Math.floor(100 / wheelSegments.length);
    const remainder = 100 - (equalProb * wheelSegments.length);

    const updated = wheelSegments.map((seg, index) => ({
      ...seg,
      probability: index === 0 ? equalProb + remainder : equalProb,
    }));
    setWheelSegments(updated);
    SuccessNotification("Probabilities distributed equally");
  }, [wheelSegments]);

  const distributeWeightedProbability = useCallback(() => {
    if (wheelSegments.length === 0) return;
    // Weighted distribution: higher value = lower probability
    const values = wheelSegments.map(s => s.value || 1);
    const maxValue = Math.max(...values);
    const weights = values.map(v => maxValue - v + 1);
    const totalWeight = weights.reduce((a, b) => a + b, 0);

    let assigned = 0;
    const updated = wheelSegments.map((seg, index) => {
      let prob;
      if (index === wheelSegments.length - 1) {
        prob = 100 - assigned;
      } else {
        prob = Math.round((weights[index] / totalWeight) * 100);
        assigned += prob;
      }
      return { ...seg, probability: Math.max(1, prob) };
    });

    setWheelSegments(updated);
    SuccessNotification("Probabilities distributed by value weight");
  }, [wheelSegments]);

  // Drag and drop handlers for wheel segments
  const handleDragStart = useCallback((index) => {
    setDraggedSegmentIndex(index);
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    if (draggedSegmentIndex === null || draggedSegmentIndex === index) return;

    const newSegments = [...wheelSegments];
    const draggedItem = newSegments[draggedSegmentIndex];
    newSegments.splice(draggedSegmentIndex, 1);
    newSegments.splice(index, 0, draggedItem);
    setWheelSegments(newSegments);
    setDraggedSegmentIndex(index);
  }, [draggedSegmentIndex, wheelSegments]);

  const handleDragEnd = useCallback(() => {
    setDraggedSegmentIndex(null);
  }, []);

  useEffect(() => {
    fetchPromotion();
  }, [id]);

  const fetchPromotion = async () => {
    try {
      const { data } = await getPromotionById(id);

      // Map server data back to form structure
      const promotion = {
        ...initialState,
        name: data.name || "",
        slug: data.slug || "",
        // Map server displayStyle to form type
        type: data.displayStyle === "banner" ? "banner" :
              data.displayStyle === "modal" ? "popup" :
              data.displayStyle === "floating" ? "floating" :
              data.displayStyle === "inline" ? "inline" : "banner",
        displayStyle: data.displayStyle || "standard",
        isActive: data.isActive ?? true,
        priority: data.priority || 5,
        // Map server string to form array
        targetAudience: data.targetAudience ? [
          data.targetAudience === "free_users" ? "free" :
          data.targetAudience === "trial_users" ? "trial" :
          data.targetAudience === "high_value" ? "paid" :
          data.targetAudience === "churned" ? "churned" :
          data.targetAudience === "new_users" ? "new" :
          data.targetAudience
        ] : ["all"],
        // Dates are at root level on server
        schedule: {
          startDate: data.startDate ? moment(data.startDate) : null,
          endDate: data.endDate ? moment(data.endDate) : null,
        },
        // Map discount fields
        discount: {
          discountType: data.discount?.type || "percentage",
          discountValue: data.discount?.value || 0,
          currency: data.discount?.currency || "USD",
          applicablePlans: data.discount?.applicablePlans || [],
        },
        coupon: {
          code: data.couponCode || "",
          autoApply: data.couponAutoApply || false,
        },
        styling: {
          theme: data.styling?.theme || "default",
          primaryColor: data.styling?.primaryColor || "#8B5CF6",
          secondaryColor: data.styling?.secondaryColor || "#06B6D4",
          animation: data.styling?.animation || "fade",
        },
        displayConditions: {
          showOnPages: data.displayConditions?.showOnPages || ["all"],
          maxImpressions: data.displayConditions?.maxShowsTotal || 0,
          maxImpressionsPerUser: data.displayConditions?.maxShowsPerDay || 3,
          delaySeconds: data.displayConditions?.delaySeconds || 0,
          scrollPercentage: data.displayConditions?.scrollPercentage || 0,
        },
        urgency: {
          showCountdown: data.urgency?.showCountdown || false,
          stockLimit: data.urgency?.stockLimit || 0,
          usageLimit: data.urgency?.usageLimit || 0,
        },
        gamification: {
          type: data.gamification?.element || "none",
          wheelSegments: data.gamification?.wheelSegments || [],
          scratchThreshold: data.gamification?.scratchThreshold || 50,
          prizeDisplayText: data.gamification?.prizeDisplayText || "You won!",
          slotSymbols: data.gamification?.slotSymbols || ["🍒", "7️⃣", "💎"],
          winningSymbol: data.gamification?.winningSymbol || "7️⃣",
          spinDuration: data.gamification?.spinDuration || 3,
          countdownPosition: data.gamification?.countdownPosition || "top",
          urgencyMessage: data.gamification?.urgencyMessage || "Hurry! Offer ends soon!",
          progressSteps: data.gamification?.progressSteps || 5,
          currentStep: data.gamification?.currentStep || 1,
          stepLabels: data.gamification?.stepLabels || [],
          // Streak config is nested under gamification.streak
          streakDays: data.gamification?.streak?.streakDays || 7,
          streakRewards: data.gamification?.streak?.streakRewards || [],
          resetOnMiss: data.gamification?.streak?.resetOnMiss !== false,
          gracePeriodHours: data.gamification?.streak?.gracePeriodHours || 0,
        },
        // Map content from server structure
        content: {
          en: {
            headline: data.content?.default?.headline || "",
            subheadline: data.content?.default?.subheadline || "",
            ctaText: data.content?.default?.ctaText || "",
            ctaSecondaryText: data.content?.default?.ctaSecondaryText || "",
            badge: data.content?.default?.badge || "",
            terms: data.content?.default?.terms || "",
          },
        },
      };

      // Add localized content
      if (data.content?.localized?.length > 0) {
        data.content.localized.forEach((loc) => {
          promotion.content[loc.locale] = {
            headline: loc.headline || "",
            subheadline: loc.subheadline || "",
            ctaText: loc.ctaText || "",
            badge: loc.badge || "",
            terms: loc.terms || "",
          };
        });
      }

      setValues(promotion);
      setWheelSegments(data.gamification?.wheelSegments || []);
      // Streak config is nested under gamification.streak
      setStreakRewards(data.gamification?.streak?.streakRewards || []);
      setStepLabels(data.gamification?.stepLabels || []);
    } catch (error) {
      console.error("Error fetching promotion:", error);
      ErrorNotification("Failed to load promotion");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setValues((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  const handleContentChange = (locale, field, value) => {
    setValues((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        [locale]: { ...prev.content[locale], [field]: value },
      },
    }));
  };

  const handleGamificationChange = (field, value) => {
    setValues((prev) => ({
      ...prev,
      gamification: { ...prev.gamification, [field]: value },
    }));
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  };

  // Normalize gamification type for comparison
  const normalizeGamificationType = (type) => {
    if (type === "wheel") return "spin_wheel";
    if (type === "scratch") return "scratch_card";
    return type;
  };

  const handleSubmit = async () => {
    // Run comprehensive validation
    const isValid = validateForm();

    if (!isValid) {
      // Show first error and navigate to relevant section
      const errorKeys = Object.keys(validationErrors);
      if (errorKeys.length > 0) {
        const firstError = errorKeys[0];
        ErrorNotification(validationErrors[firstError]);

        // Navigate to the appropriate section based on error type
        if (firstError === "name") {
          setActiveSection("basic");
        } else if (firstError === "schedule") {
          setActiveSection("schedule");
        } else if (firstError === "headline") {
          setActiveSection("content");
        } else if (firstError.includes("wheel") || firstError.includes("slot") || firstError.includes("streak")) {
          setActiveSection("gamification");
        }
      }
      return;
    }

    // Additional gamification-specific validation (backward compatibility)
    const normalizedType = normalizeGamificationType(values.gamification?.type);
    if ((normalizedType === "spin_wheel") && probabilitySum !== 100) {
      ErrorNotification("Wheel segment probabilities must sum to exactly 100%");
      setActiveSection("gamification");
      return;
    }

    setSaving(true);
    try {
      // Build gamification config based on type
      const gamificationConfig = {
        enabled: values.gamification?.type !== "none",
        element: normalizeGamificationType(values.gamification?.type) || "none",
        maxPlaysPerUser: 1,
      };

      // Add type-specific config
      if (normalizedType === "spin_wheel") {
        gamificationConfig.wheelSegments = wheelSegments;
      } else if (normalizedType === "scratch_card") {
        gamificationConfig.scratchThreshold = values.gamification?.scratchThreshold || 50;
        gamificationConfig.prizeDisplayText = values.gamification?.prizeDisplayText || "You won!";
      } else if (normalizedType === "slot_machine") {
        gamificationConfig.slotSymbols = values.gamification?.slotSymbols || ["🍒", "7️⃣", "💎"];
        gamificationConfig.winningSymbol = values.gamification?.winningSymbol || "7️⃣";
        gamificationConfig.spinDuration = values.gamification?.spinDuration || 3;
      } else if (normalizedType === "countdown") {
        gamificationConfig.countdownPosition = values.gamification?.countdownPosition || "top";
        gamificationConfig.urgencyMessage = values.gamification?.urgencyMessage || "Hurry! Offer ends soon!";
      } else if (normalizedType === "progress_bar") {
        gamificationConfig.progressSteps = values.gamification?.progressSteps || 5;
        gamificationConfig.currentStep = values.gamification?.currentStep || 1;
        gamificationConfig.stepLabels = stepLabels;
      } else if (normalizedType === "streak") {
        // Server expects streak config nested in gamification.streak
        gamificationConfig.streak = {
          streakDays: values.gamification?.streakDays || 7,
          streakRewards: streakRewards,
          resetOnMiss: values.gamification?.resetOnMiss !== false,
          gracePeriodHours: values.gamification?.gracePeriodHours || 0,
        };
      }

      // Map form values to server schema
      const payload = {
        name: values.name,
        slug: values.slug || generateSlug(values.name),

        // Map form type to server's type enum
        type: values.type === "banner" ? "seasonal" :
              values.type === "popup" ? "seasonal" :
              values.type === "floating" ? "flash_sale" :
              values.type === "inline" ? "milestone" :
              values.type === "exit_intent" ? "win_back" :
              values.type === "gamified" ? "gamified" : "seasonal",

        // Map form type to displayStyle
        displayStyle: values.type === "banner" ? "banner" :
                      values.type === "popup" ? "modal" :
                      values.type === "floating" ? "floating" :
                      values.type === "inline" ? "inline" :
                      values.type === "exit_intent" ? "modal" :
                      values.type === "gamified" ? "modal" : "banner",

        isActive: values.isActive,
        priority: values.priority,

        // Target audience - server expects string, not array
        targetAudience: Array.isArray(values.targetAudience)
          ? (values.targetAudience.includes("all") ? "all" :
             values.targetAudience.includes("free") ? "free_users" :
             values.targetAudience.includes("trial") ? "trial_users" :
             values.targetAudience.includes("paid") ? "high_value" :
             values.targetAudience.includes("churned") ? "churned" :
             values.targetAudience.includes("new") ? "new_users" :
             values.targetAudience[0] || "all")
          : values.targetAudience || "all",

        // Dates - extract to root level
        startDate: values.schedule.startDate?.toISOString?.()
          || values.schedule.startDate?.format?.()
          || values.schedule.startDate,
        endDate: values.schedule.endDate?.toISOString?.()
          || values.schedule.endDate?.format?.()
          || values.schedule.endDate,

        // Discount - map field names
        discount: {
          type: values.discount.discountType || "percentage",
          value: values.discount.discountValue || 0,
          currency: values.discount.currency || "USD",
        },

        // Coupon
        couponCode: values.coupon.code || undefined,
        couponAutoApply: values.coupon.autoApply || false,

        // Styling
        styling: {
          theme: values.styling.theme || "dark",
          primaryColor: values.styling.primaryColor || "#8B5CF6",
          secondaryColor: values.styling.secondaryColor || "#06B6D4",
          animation: values.styling.animation || "fade",
        },

        // Display conditions
        displayConditions: {
          showOnPages: values.displayConditions.showOnPages || ["all"],
          maxShowsTotal: values.displayConditions.maxImpressions || null,
          maxShowsPerDay: values.displayConditions.maxImpressionsPerUser || null,
          delaySeconds: values.displayConditions.delaySeconds || 0,
          scrollPercentage: values.displayConditions.scrollPercentage || null,
        },

        // Urgency
        urgency: {
          showCountdown: values.urgency.showCountdown || false,
          stockLimit: values.urgency.stockLimit || null,
        },

        // Gamification
        gamification: gamificationConfig,

        // Content - map to server schema structure
        content: {
          default: {
            locale: "en",
            headline: values.content.en?.headline || values.name,
            subheadline: values.content.en?.subheadline || "",
            ctaText: values.content.en?.ctaText || "Get Started",
            badge: values.content.en?.badge || "",
            terms: values.content.en?.terms || "",
          },
          localized: Object.entries(values.content)
            .filter(([locale]) => locale !== "en")
            .map(([locale, content]) => ({
              locale,
              headline: content.headline || "",
              subheadline: content.subheadline || "",
              ctaText: content.ctaText || "Get Started",
              badge: content.badge || "",
              terms: content.terms || "",
            })),
        },
      };

      await updatePromotionById(id, payload);
      SuccessNotification("Promotion updated successfully");
      history.push("/super-admin/promotions/list");
    } catch (error) {
      console.error("Update promotion error:", error);
      ErrorNotification(error.response?.data?.message || "Error updating promotion");
    } finally {
      setSaving(false);
    }
  };

  // Wheel Segment handlers
  const addWheelSegment = () => {
    const newColor = segmentColors[wheelSegments.length % segmentColors.length];
    setWheelSegments([...wheelSegments, { label: "", value: 0, probability: 0, color: newColor }]);
  };

  const updateWheelSegment = (index, field, value) => {
    const updated = [...wheelSegments];
    updated[index][field] = value;
    setWheelSegments(updated);
  };

  const removeWheelSegment = (index) => {
    setWheelSegments(wheelSegments.filter((_, i) => i !== index));
  };

  // Streak Reward handlers
  const addStreakReward = () => {
    const nextDay = streakRewards.length > 0
      ? Math.max(...streakRewards.map(r => r.day)) + 1
      : 1;
    setStreakRewards([...streakRewards, { day: nextDay, reward: `Day ${nextDay} Bonus`, value: "", type: "credits" }]);
  };

  const updateStreakReward = (index, field, value) => {
    const updated = [...streakRewards];
    updated[index][field] = value;
    setStreakRewards(updated);
  };

  const removeStreakReward = (index) => {
    setStreakRewards(streakRewards.filter((_, i) => i !== index));
  };

  // Step Label handlers
  const updateStepLabels = (steps) => {
    const currentLabels = [...stepLabels];
    while (currentLabels.length < steps) {
      currentLabels.push(`Step ${currentLabels.length + 1}`);
    }
    setStepLabels(currentLabels.slice(0, steps));
  };

  const updateStepLabel = (index, value) => {
    const updated = [...stepLabels];
    updated[index] = value;
    setStepLabels(updated);
  };

  // Symbol picker for slot machine
  const toggleSlotSymbol = (emoji) => {
    const current = values.gamification?.slotSymbols || [];
    if (current.includes(emoji)) {
      if (current.length > 2) {
        handleGamificationChange("slotSymbols", current.filter(s => s !== emoji));
      }
    } else {
      handleGamificationChange("slotSymbols", [...current, emoji]);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  const renderGamificationConfig = () => {
    const gamificationType = normalizeGamificationType(values.gamification?.type);

    switch (gamificationType) {
      case "spin_wheel":
        return (
          <div style={styles.configCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={styles.configTitle}>
                <span>🎡</span> Wheel Segments Configuration
              </div>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => resetGamificationToDefaults("spin_wheel")}
                style={styles.resetButton}
              >
                Reset to Defaults
              </Button>
            </div>

            <Alert
              message="Probability Must Equal 100%"
              description="The sum of all segment probabilities must equal exactly 100%. Drag segments to reorder them on the wheel."
              type="info"
              showIcon
              style={{ marginBottom: 16, borderRadius: 8 }}
            />

            {/* Quick Probability Distribution Buttons */}
            <div style={styles.distributionButtonGroup}>
              <Tooltip title="Distribute probabilities equally among all segments">
                <Button
                  onClick={distributeEqualProbability}
                  style={styles.distributionButton}
                  icon={<SwapOutlined />}
                >
                  Equal Distribution
                </Button>
              </Tooltip>
              <Tooltip title="Higher value segments get lower probability (more realistic)">
                <Button
                  onClick={distributeWeightedProbability}
                  style={styles.distributionButton}
                  icon={<SwapOutlined />}
                >
                  Weighted by Value
                </Button>
              </Tooltip>
            </div>

            {/* Draggable Segments */}
            {wheelSegments.map((segment, index) => (
              <div
                key={index}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  ...styles.segmentRowDraggable,
                  ...(draggedSegmentIndex === index ? styles.segmentRowDragging : {}),
                }}
              >
                {/* Drag Handle */}
                <div style={styles.dragHandle}>
                  <HolderOutlined />
                </div>

                {/* Segment Number */}
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: segment.color || segmentColors[index % segmentColors.length],
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 600,
                }}>
                  {index + 1}
                </div>

                {/* Label Input */}
                <div style={{ flex: 2 }}>
                  <Input
                    placeholder="Label (e.g., 10% Off)"
                    value={segment.label}
                    onChange={(e) => updateWheelSegment(index, "label", e.target.value)}
                    style={{ borderRadius: 6 }}
                    status={!segment.label?.trim() ? "error" : ""}
                  />
                </div>

                {/* Value Input */}
                <div style={{ flex: 1 }}>
                  <InputNumber
                    placeholder="Value"
                    min={0}
                    value={segment.value}
                    onChange={(value) => updateWheelSegment(index, "value", value)}
                    style={{ width: "100%", borderRadius: 6 }}
                    addonAfter="%"
                  />
                </div>

                {/* Probability Input */}
                <div style={{ flex: 1 }}>
                  <InputNumber
                    placeholder="Chance"
                    min={0}
                    max={100}
                    value={segment.probability}
                    onChange={(value) => updateWheelSegment(index, "probability", value)}
                    style={{ width: "100%", borderRadius: 6 }}
                    addonAfter="%"
                    status={segment.probability === 0 ? "warning" : ""}
                  />
                </div>

                {/* Enhanced Color Picker */}
                <div style={styles.colorPickerEnhanced}>
                  <Popover
                    content={
                      <div>
                        <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 12 }}>Preset Colors</div>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", maxWidth: 200, marginBottom: 12 }}>
                          {segmentColors.map((color) => (
                            <div
                              key={color}
                              onClick={() => updateWheelSegment(index, "color", color)}
                              style={{
                                ...styles.colorSwatch(color),
                                width: 28,
                                height: 28,
                                border: segment.color === color ? "3px solid #000" : "2px solid #fff",
                                boxShadow: segment.color === color ? "0 0 0 1px #000" : "0 0 0 1px #d9d9d9",
                              }}
                            />
                          ))}
                        </div>
                        <Divider style={{ margin: "8px 0" }} />
                        <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 12 }}>Custom Color</div>
                        <input
                          type="color"
                          value={segment.color || segmentColors[index % segmentColors.length]}
                          onChange={(e) => updateWheelSegment(index, "color", e.target.value)}
                          style={{ width: "100%", height: 32, cursor: "pointer", borderRadius: 4, border: "none" }}
                        />
                      </div>
                    }
                    trigger="click"
                    placement="bottom"
                  >
                    <div
                      style={{
                        ...styles.colorSwatch(segment.color || segmentColors[index % segmentColors.length]),
                        cursor: "pointer",
                      }}
                    />
                  </Popover>
                </div>

                {/* Delete Button */}
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeWheelSegment(index)}
                  disabled={wheelSegments.length <= 2}
                />
              </div>
            ))}

            <Button
              type="dashed"
              onClick={addWheelSegment}
              icon={<PlusOutlined />}
              style={{ width: "100%", borderRadius: 8, height: 44 }}
            >
              Add Segment
            </Button>

            {/* Validation Status */}
            {probabilitySum !== 100 && (
              <div style={styles.validationError}>
                <Space>
                  <WarningOutlined style={{ color: "#DC2626" }} />
                  <Text style={{ color: "#991B1B" }}>
                    Probability total: <strong>{probabilitySum}%</strong> - Must equal exactly 100%.
                    {probabilitySum < 100 && ` Add ${100 - probabilitySum}% more.`}
                    {probabilitySum > 100 && ` Remove ${probabilitySum - 100}%.`}
                  </Text>
                </Space>
              </div>
            )}

            {probabilitySum === 100 && wheelSegments.every(s => s.label?.trim()) && (
              <div style={styles.validationSuccess}>
                <Space>
                  <CheckCircleOutlined style={{ color: "#16A34A" }} />
                  <Text style={{ color: "#166534" }}>
                    Configuration is valid! {wheelSegments.length} segments totaling 100%.
                  </Text>
                </Space>
              </div>
            )}

            {wheelSegments.some(s => !s.label?.trim()) && (
              <div style={styles.validationError}>
                <Space>
                  <WarningOutlined style={{ color: "#DC2626" }} />
                  <Text style={{ color: "#991B1B" }}>
                    All segments must have labels. {wheelSegments.filter(s => !s.label?.trim()).length} segment(s) missing labels.
                  </Text>
                </Space>
              </div>
            )}
          </div>
        );

      case "scratch_card":
        return (
          <div style={styles.configCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={styles.configTitle}>
                <span>🎴</span> Scratch Card Configuration
              </div>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => resetGamificationToDefaults("scratch_card")}
                style={styles.resetButton}
              >
                Reset to Defaults
              </Button>
            </div>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Scratch Threshold
                    <Tooltip title="Percentage of card that must be scratched to reveal prize">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <div>
                    <Slider
                      min={10}
                      max={90}
                      value={values.gamification?.scratchThreshold || 50}
                      onChange={(value) => handleGamificationChange("scratchThreshold", value)}
                      marks={{
                        10: "10%",
                        50: "50%",
                        90: "90%",
                      }}
                    />
                    <div style={{ textAlign: "center", marginTop: 8 }}>
                      <Tag color="blue">{values.gamification?.scratchThreshold || 50}% scratched to reveal</Tag>
                    </div>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Prize Display Text</div>
                  <Input
                    size="large"
                    placeholder="e.g., Congratulations! You won:"
                    value={values.gamification?.prizeDisplayText || ""}
                    onChange={(e) => handleGamificationChange("prizeDisplayText", e.target.value)}
                    style={{ borderRadius: 8 }}
                  />
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                    Text shown when prize is revealed
                  </Text>
                </div>
              </Col>
            </Row>
          </div>
        );

      case "slot_machine":
        return (
          <div style={styles.configCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={styles.configTitle}>
                <span>🎰</span> Slot Machine Configuration
              </div>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => resetGamificationToDefaults("slot_machine")}
                style={styles.resetButton}
              >
                Reset to Defaults
              </Button>
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>
                Select Symbols
                <Tooltip title="Choose at least 3 symbols for the slot machine reels">
                  <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                </Tooltip>
              </div>
              <div style={styles.symbolGrid}>
                {slotSymbols.map(({ emoji, label }) => (
                  <Tooltip key={emoji} title={label}>
                    <div
                      style={styles.symbolButton((values.gamification?.slotSymbols || []).includes(emoji))}
                      onClick={() => toggleSlotSymbol(emoji)}
                    >
                      {emoji}
                    </div>
                  </Tooltip>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Selected: </Text>
                {(values.gamification?.slotSymbols || ["🍒", "7️⃣", "💎"]).map((emoji, i) => (
                  <Tag key={i} style={{ fontSize: 16, padding: "4px 8px" }}>{emoji}</Tag>
                ))}
              </div>
            </div>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Winning Symbol
                    <Tooltip title="3 of this symbol in a row = jackpot!">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <Select
                    size="large"
                    value={values.gamification?.winningSymbol || "7️⃣"}
                    onChange={(value) => handleGamificationChange("winningSymbol", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    {(values.gamification?.slotSymbols || ["🍒", "7️⃣", "💎"]).map((emoji) => (
                      <Option key={emoji} value={emoji}>
                        <span style={{ fontSize: 18 }}>{emoji}</span>
                        <span style={{ marginLeft: 8 }}>
                          {slotSymbols.find(s => s.emoji === emoji)?.label || emoji}
                        </span>
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Spin Duration
                    <Tooltip title="How long the reels spin (in seconds)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={1}
                    max={10}
                    value={values.gamification?.spinDuration || 3}
                    onChange={(value) => handleGamificationChange("spinDuration", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    addonAfter="seconds"
                  />
                </div>
              </Col>
            </Row>
          </div>
        );

      case "countdown":
        return (
          <div style={styles.configCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={styles.configTitle}>
                <span>⏰</span> Countdown Timer Configuration
              </div>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => resetGamificationToDefaults("countdown")}
                style={styles.resetButton}
              >
                Reset to Defaults
              </Button>
            </div>

            <Alert
              message="Uses Schedule End Date"
              description="The countdown timer will count down to the end date specified in the Schedule section."
              type="info"
              showIcon
              style={{ marginBottom: 16, borderRadius: 8 }}
            />

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Countdown Position</div>
                  <Select
                    size="large"
                    value={values.gamification?.countdownPosition || "top"}
                    onChange={(value) => handleGamificationChange("countdownPosition", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    {countdownPositions.map((pos) => (
                      <Option key={pos.value} value={pos.value}>{pos.label}</Option>
                    ))}
                  </Select>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Urgency Message</div>
                  <Input
                    size="large"
                    placeholder="e.g., Hurry! Offer ends soon!"
                    value={values.gamification?.urgencyMessage || ""}
                    onChange={(e) => handleGamificationChange("urgencyMessage", e.target.value)}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              </Col>
            </Row>

            {values.schedule.endDate && (
              <div style={{ marginTop: 16, padding: 12, background: "#EFF6FF", borderRadius: 8 }}>
                <Text type="secondary">Counting down to: </Text>
                <Text strong>{values.schedule.endDate.format?.("MMMM D, YYYY h:mm A") || "Set in Schedule"}</Text>
              </div>
            )}
          </div>
        );

      case "progress_bar":
        return (
          <div style={styles.configCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={styles.configTitle}>
                <span>📊</span> Progress Bar Configuration
              </div>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => resetGamificationToDefaults("progress_bar")}
                style={styles.resetButton}
              >
                Reset to Defaults
              </Button>
            </div>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Total Steps
                    <Tooltip title="Total number of steps in the progress bar">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={2}
                    max={10}
                    value={values.gamification?.progressSteps || 5}
                    onChange={(value) => {
                      handleGamificationChange("progressSteps", value);
                      updateStepLabels(value);
                    }}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Current Step
                    <Tooltip title="Where the user currently is in the progress">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    max={values.gamification?.progressSteps || 5}
                    value={values.gamification?.currentStep || 1}
                    onChange={(value) => handleGamificationChange("currentStep", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
            </Row>

            <Divider style={{ margin: "16px 0" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Step Labels (Optional)</Text>
            </Divider>

            {Array.from({ length: values.gamification?.progressSteps || 5 }).map((_, index) => (
              <div key={index} style={styles.stepLabelRow}>
                <Tag color="blue" style={{ minWidth: 60, textAlign: "center" }}>Step {index + 1}</Tag>
                <Input
                  placeholder={`Label for step ${index + 1}`}
                  value={stepLabels[index] || ""}
                  onChange={(e) => updateStepLabel(index, e.target.value)}
                  style={{ flex: 1, borderRadius: 6 }}
                />
              </div>
            ))}

            {/* Progress Preview */}
            <div style={{ marginTop: 16, padding: 16, background: "#fff", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>Preview:</Text>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {Array.from({ length: values.gamification?.progressSteps || 5 }).map((_, index) => (
                  <div key={index} style={{ flex: 1, display: "flex", alignItems: "center" }}>
                    <div style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: index < (values.gamification?.currentStep || 1) ? "#10B981" : "#e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                    }}>
                      {index < (values.gamification?.currentStep || 1) ? "✓" : index + 1}
                    </div>
                    {index < (values.gamification?.progressSteps || 5) - 1 && (
                      <div style={{
                        flex: 1,
                        height: 4,
                        background: index < (values.gamification?.currentStep || 1) - 1 ? "#10B981" : "#e5e7eb",
                        marginLeft: 4,
                      }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "streak":
        return (
          <div style={styles.configCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={styles.configTitle}>
                <span>🔥</span> Daily Streak Configuration
              </div>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => resetGamificationToDefaults("streak")}
                style={styles.resetButton}
              >
                Reset to Defaults
              </Button>
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>
                Target Streak Days
                <Tooltip title="Number of consecutive days to complete the streak">
                  <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                </Tooltip>
              </div>
              <InputNumber
                size="large"
                min={1}
                max={30}
                value={values.gamification?.streakDays || 7}
                onChange={(value) => handleGamificationChange("streakDays", value)}
                style={{ width: "50%", borderRadius: 8 }}
                addonAfter="days"
              />
            </div>

            <Divider style={{ margin: "16px 0" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Streak Rewards</Text>
            </Divider>

            <Alert
              message="Configure Milestone Rewards"
              description="Set rewards for specific days in the streak. Users will see what they can earn at each milestone."
              type="info"
              showIcon
              style={{ marginBottom: 16, borderRadius: 8 }}
            />

            {/* Column Headers */}
            <div style={{ display: "flex", gap: 12, padding: "0 16px", marginBottom: 8 }}>
              <div style={{ width: 70 }}>
                <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>DAY</Text>
              </div>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>MILESTONE NAME</Text>
              </div>
              <div style={{ width: 120 }}>
                <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>TYPE</Text>
              </div>
              <div style={{ width: 100 }}>
                <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>VALUE</Text>
              </div>
              <div style={{ width: 32 }}></div>
            </div>

            {streakRewards.map((reward, index) => (
              <div key={index} style={styles.streakRewardRow}>
                <div style={{ width: 70 }}>
                  <InputNumber
                    min={1}
                    max={values.gamification?.streakDays || 7}
                    value={reward.day}
                    onChange={(value) => updateStreakReward(index, "day", value)}
                    style={{ width: "100%", borderRadius: 6 }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Input
                    placeholder="e.g., Day 3 Momentum"
                    value={reward.reward}
                    onChange={(e) => updateStreakReward(index, "reward", e.target.value)}
                    style={{ borderRadius: 6 }}
                  />
                </div>
                <div style={{ width: 120 }}>
                  <Select
                    value={reward.type || "credits"}
                    onChange={(val) => updateStreakReward(index, "type", val)}
                    style={{ width: "100%" }}
                    size="middle"
                  >
                    <Option value="credits">Credits</Option>
                    <Option value="discount_percentage">% Discount</Option>
                    <Option value="discount_fixed">Fixed Discount</Option>
                    <Option value="free_trial">Free Trial</Option>
                  </Select>
                </div>
                <div style={{ width: 100 }}>
                  <InputNumber
                    placeholder="e.g., 10"
                    value={reward.value}
                    onChange={(val) => updateStreakReward(index, "value", val)}
                    style={{ width: "100%", borderRadius: 6 }}
                    min={0}
                  />
                </div>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeStreakReward(index)}
                />
              </div>
            ))}

            <Button
              type="dashed"
              onClick={addStreakReward}
              icon={<PlusOutlined />}
              style={{ width: "100%", borderRadius: 8, height: 44 }}
            >
              Add Reward Milestone
            </Button>

            {/* Streak Preview */}
            <div style={{ marginTop: 16, padding: 16, background: "#fff", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>Preview:</Text>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Array.from({ length: values.gamification?.streakDays || 7 }).map((_, index) => {
                  const dayNum = index + 1;
                  const reward = streakRewards.find(r => r.day === dayNum);
                  return (
                    <div key={index} style={{
                      width: 60,
                      padding: 8,
                      borderRadius: 8,
                      background: reward ? "#FEF3C7" : "#f9fafb",
                      border: reward ? "2px solid #F59E0B" : "1px solid #e5e7eb",
                      textAlign: "center",
                    }}>
                      <div style={{ fontSize: 18 }}>{reward ? "🎁" : "📅"}</div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>Day {dayNum}</div>
                      {reward && (
                        <div style={{ fontSize: 10, color: "#92400E" }}>{reward.value}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case "basic":
        return (
          <>
            <div style={styles.sectionTitle}>Basic Information</div>
            <div style={styles.sectionDescription}>
              Update the fundamental details of your promotion
            </div>

            <Row gutter={24}>
              <Col span={16}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Promotion Name <Text type="danger">*</Text>
                  </div>
                  <Input
                    size="large"
                    placeholder="e.g., Black Friday Sale 2024"
                    value={values.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    onBlur={() => {
                      if (!values.slug) handleChange("slug", generateSlug(values.name));
                    }}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Slug
                    <Tooltip title="URL-friendly identifier">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <Input
                    size="large"
                    placeholder="black-friday-2024"
                    value={values.slug}
                    onChange={(e) => handleChange("slug", e.target.value)}
                    prefix={<span style={{ color: "#9ca3af" }}>/</span>}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Promotion Type</div>
                  <Select
                    size="large"
                    value={values.type}
                    onChange={(value) => handleChange("type", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    {promotionTypes.map((type) => (
                      <Option key={type.value} value={type.value}>
                        <Space>
                          <span>{type.icon}</span>
                          <span>{type.label}</span>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            - {type.description}
                          </Text>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Display Style</div>
                  <Select
                    size="large"
                    value={values.displayStyle}
                    onChange={(value) => handleChange("displayStyle", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    {displayStyles.map((style) => (
                      <Option key={style.value} value={style.value}>
                        <Space>
                          <span>{style.label}</span>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            - {style.description}
                          </Text>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Priority
                    <Tooltip title="Higher priority promotions are shown first (1-10)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={1}
                    max={10}
                    value={values.priority}
                    onChange={(value) => handleChange("priority", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Status</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                    <Switch
                      checked={values.isActive}
                      onChange={(checked) => handleChange("isActive", checked)}
                    />
                    <Badge
                      status={values.isActive ? "success" : "default"}
                      text={values.isActive ? "Active" : "Inactive"}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </>
        );

      case "targeting":
        return (
          <>
            <div style={styles.sectionTitle}>Audience Targeting</div>
            <div style={styles.sectionDescription}>
              Define who should see this promotion
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Target Audience</div>
              <Select
                mode="multiple"
                size="large"
                value={values.targetAudience}
                onChange={(value) => handleChange("targetAudience", value)}
                placeholder="Select target audiences"
                style={{ width: "100%", borderRadius: 8 }}
              >
                {targetAudiences.map((audience) => (
                  <Option key={audience.value} value={audience.value}>
                    <Space>
                      <span>{audience.icon}</span>
                      <span>{audience.label}</span>
                    </Space>
                  </Option>
                ))}
              </Select>
            </div>

            <Divider style={{ margin: "24px 0" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Advanced Targeting</Text>
            </Divider>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Excluded User IDs
                    <Tooltip title="These users will never see this promotion">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <Select
                    mode="tags"
                    size="large"
                    value={values.excludedUserIds}
                    onChange={(value) => handleChange("excludedUserIds", value)}
                    placeholder="Enter user IDs to exclude"
                    tokenSeparators={[","]}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Included User IDs
                    <Tooltip title="These users will always see this promotion (overrides other rules)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <Select
                    mode="tags"
                    size="large"
                    value={values.includedUserIds}
                    onChange={(value) => handleChange("includedUserIds", value)}
                    placeholder="Enter user IDs to always include"
                    tokenSeparators={[","]}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
            </Row>
          </>
        );

      case "schedule":
        return (
          <>
            <div style={styles.sectionTitle}>Schedule</div>
            <div style={styles.sectionDescription}>
              Set when this promotion should be active
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Promotion Period <Text type="danger">*</Text></div>
              <RangePicker
                size="large"
                showTime
                style={{ width: "100%", borderRadius: 8 }}
                value={[values.schedule.startDate, values.schedule.endDate]}
                onChange={(dates) => {
                  setValues((prev) => ({
                    ...prev,
                    schedule: {
                      ...prev.schedule,
                      startDate: dates?.[0] || null,
                      endDate: dates?.[1] || null,
                    },
                  }));
                }}
              />
            </div>

            <Alert
              message="Schedule Required"
              description="Start and end dates are required. The promotion will automatically activate and deactivate based on these dates."
              type="warning"
              showIcon
              style={{ borderRadius: 8 }}
            />
          </>
        );

      case "discount":
        return (
          <>
            <div style={styles.sectionTitle}>
              {values.discount?.currency === "credits" ? "Credit Reward Settings" : "Discount Settings"}
            </div>
            <div style={styles.sectionDescription}>
              {values.discount?.currency === "credits"
                ? "Configure the credit reward for this promotion"
                : "Configure the discount offer for this promotion"}
            </div>

            <Row gutter={24}>
              {values.discount?.currency !== "credits" && (
                <Col span={8}>
                  <div style={styles.formGroup}>
                    <div style={styles.formLabel}>Discount Type</div>
                    <Select
                      size="large"
                      value={values.discount?.discountType}
                      onChange={(value) => handleNestedChange("discount", "discountType", value)}
                      style={{ width: "100%", borderRadius: 8 }}
                    >
                      <Option value="percentage">Percentage (%)</Option>
                      <Option value="fixed_amount">Fixed Amount</Option>
                    </Select>
                  </div>
                </Col>
              )}
              <Col span={values.discount?.currency === "credits" ? 12 : 8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    {values.discount?.currency === "credits" ? "Credit Amount" : "Discount Value"}
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    max={values.discount?.discountType === "percentage" && values.discount?.currency !== "credits" ? 100 : undefined}
                    value={values.discount?.discountValue}
                    onChange={(value) => handleNestedChange("discount", "discountValue", value)}
                    addonAfter={
                      values.discount?.currency === "credits"
                        ? "⭐"
                        : values.discount?.discountType === "percentage"
                        ? "%"
                        : values.discount?.currency
                    }
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={values.discount?.currency === "credits" ? 12 : 8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    {values.discount?.currency === "credits" ? "Reward Type" : "Currency"}
                  </div>
                  <Select
                    size="large"
                    value={values.discount?.currency}
                    onChange={(value) => handleNestedChange("discount", "currency", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    <Option value="credits">
                      <Space>
                        <span>⭐</span>
                        <span>Credits (Gamification)</span>
                      </Space>
                    </Option>
                    <Option value="USD">USD ($)</Option>
                    <Option value="EUR">EUR (€)</Option>
                    <Option value="GBP">GBP (£)</Option>
                  </Select>
                </div>
              </Col>
            </Row>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Applicable Plans</div>
              <Select
                mode="multiple"
                size="large"
                value={values.discount?.applicablePlans}
                onChange={(value) => handleNestedChange("discount", "applicablePlans", value)}
                placeholder="Leave empty for all plans"
                style={{ width: "100%", borderRadius: 8 }}
              >
                <Option value="starter">Starter</Option>
                <Option value="pro">Pro</Option>
                <Option value="enterprise">Enterprise</Option>
              </Select>
            </div>

            <Divider style={{ margin: "24px 0" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Coupon Code</Text>
            </Divider>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Coupon Code</div>
                  <Input
                    size="large"
                    placeholder="e.g., BLACKFRIDAY50"
                    value={values.coupon?.code}
                    onChange={(e) => handleNestedChange("coupon", "code", e.target.value.toUpperCase())}
                    style={{ borderRadius: 8, fontFamily: "monospace", letterSpacing: 1 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Auto Apply</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                    <Switch
                      checked={values.coupon?.autoApply}
                      onChange={(checked) => handleNestedChange("coupon", "autoApply", checked)}
                    />
                    <Text type="secondary">Automatically apply at checkout</Text>
                  </div>
                </div>
              </Col>
            </Row>
          </>
        );

      case "styling":
        return (
          <>
            <div style={styles.sectionTitle}>Visual Styling</div>
            <div style={styles.sectionDescription}>
              Customize the look and feel of your promotion
            </div>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Theme</div>
                  <Select
                    size="large"
                    value={values.styling?.theme}
                    onChange={(value) => handleNestedChange("styling", "theme", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    {themeOptions.map((theme) => (
                      <Option key={theme.value} value={theme.value}>
                        <Space>
                          <div style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            background: theme.color,
                            border: "1px solid #e5e7eb",
                          }} />
                          <span>{theme.label}</span>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Animation</div>
                  <Select
                    size="large"
                    value={values.styling?.animation}
                    onChange={(value) => handleNestedChange("styling", "animation", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    {animationOptions.map((anim) => (
                      <Option key={anim.value} value={anim.value}>{anim.label}</Option>
                    ))}
                  </Select>
                </div>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Primary Color</div>
                  <div style={styles.colorPickerWrapper}>
                    <input
                      type="color"
                      value={values.styling?.primaryColor || "#8B5CF6"}
                      onChange={(e) => handleNestedChange("styling", "primaryColor", e.target.value)}
                      style={{ width: 40, height: 40, border: "none", cursor: "pointer", borderRadius: 6 }}
                    />
                    <Input
                      value={values.styling?.primaryColor}
                      onChange={(e) => handleNestedChange("styling", "primaryColor", e.target.value)}
                      style={{ border: "none", flex: 1, fontFamily: "monospace" }}
                    />
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Secondary Color</div>
                  <div style={styles.colorPickerWrapper}>
                    <input
                      type="color"
                      value={values.styling?.secondaryColor || "#06B6D4"}
                      onChange={(e) => handleNestedChange("styling", "secondaryColor", e.target.value)}
                      style={{ width: 40, height: 40, border: "none", cursor: "pointer", borderRadius: 6 }}
                    />
                    <Input
                      value={values.styling?.secondaryColor}
                      onChange={(e) => handleNestedChange("styling", "secondaryColor", e.target.value)}
                      style={{ border: "none", flex: 1, fontFamily: "monospace" }}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </>
        );

      case "display":
        return (
          <>
            <div style={styles.sectionTitle}>Display Rules</div>
            <div style={styles.sectionDescription}>
              Control where and how the promotion appears
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Show On Pages</div>
              <Checkbox.Group
                value={values.displayConditions?.showOnPages}
                onChange={(value) => handleNestedChange("displayConditions", "showOnPages", value)}
              >
                <Row gutter={[16, 12]}>
                  {showOnPagesOptions.map((page) => (
                    <Col span={8} key={page.value}>
                      <Checkbox value={page.value} style={{ padding: "8px 0" }}>
                        {page.label}
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </div>

            <Divider style={{ margin: "24px 0" }} />

            <Row gutter={24}>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Max Impressions
                    <Tooltip title="Total times this promotion can be shown (0 = unlimited)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    value={values.displayConditions?.maxImpressions}
                    onChange={(value) => handleNestedChange("displayConditions", "maxImpressions", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    placeholder="0 = unlimited"
                  />
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Max Per Day
                    <Tooltip title="Maximum times per day a user can see this. For daily bonuses (credit rewards), set to 1 - the promotion will reset and show again the next day.">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    value={values.displayConditions?.maxImpressionsPerUser}
                    onChange={(value) => handleNestedChange("displayConditions", "maxImpressionsPerUser", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    placeholder="1 = daily"
                  />
                  {values.discount?.currency === "credits" && values.displayConditions?.maxImpressionsPerUser === 1 && (
                    <div style={{ marginTop: 4, fontSize: 11, color: "#10B981" }}>
                      ✓ Daily recurring promotion (resets each day)
                    </div>
                  )}
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Delay (seconds)
                    <Tooltip title="Wait time before showing promotion">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    value={values.displayConditions?.delaySeconds}
                    onChange={(value) => handleNestedChange("displayConditions", "delaySeconds", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    addonAfter="sec"
                  />
                </div>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Scroll Trigger
                    <Tooltip title="Show promotion after user scrolls this percentage (0 = immediate)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    max={100}
                    value={values.displayConditions?.scrollPercentage}
                    onChange={(value) => handleNestedChange("displayConditions", "scrollPercentage", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    addonAfter="%"
                  />
                </div>
              </Col>
            </Row>
          </>
        );

      case "urgency":
        return (
          <>
            <div style={styles.sectionTitle}>Urgency Elements</div>
            <div style={styles.sectionDescription}>
              Add urgency to drive faster conversions
            </div>

            <Row gutter={24}>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Show Countdown</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                    <Switch
                      checked={values.urgency?.showCountdown}
                      onChange={(checked) => handleNestedChange("urgency", "showCountdown", checked)}
                    />
                    <Text type="secondary">Display countdown timer</Text>
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Stock Limit
                    <Tooltip title="Shows 'Only X left!' message (0 = disabled)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    value={values.urgency?.stockLimit}
                    onChange={(value) => handleNestedChange("urgency", "stockLimit", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Usage Limit
                    <Tooltip title="Max conversions allowed (0 = unlimited)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    value={values.urgency?.usageLimit}
                    onChange={(value) => handleNestedChange("urgency", "usageLimit", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
            </Row>
          </>
        );

      case "gamification":
        return (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
              <div>
                <div style={styles.sectionTitle}>Gamification</div>
                <div style={styles.sectionDescription}>
                  Add interactive elements to engage users and boost conversions
                </div>
              </div>
              <Dropdown
                overlay={
                  <Menu>
                    {gamificationTemplates.map((template) => (
                      <Menu.Item
                        key={template.id}
                        onClick={() => applyTemplate(template)}
                      >
                        <Space>
                          <span style={{ fontSize: 18 }}>{template.icon}</span>
                          <div>
                            <div style={{ fontWeight: 500 }}>{template.name}</div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>{template.description}</div>
                          </div>
                        </Space>
                      </Menu.Item>
                    ))}
                  </Menu>
                }
                trigger={["click"]}
              >
                <Button icon={<FileAddOutlined />}>
                  Load Template <DownOutlined />
                </Button>
              </Dropdown>
            </div>

            {/* Template Quick Select Cards */}
            <Alert
              message="Quick Start with Templates"
              description="Select a pre-configured template to quickly set up your gamification with optimized settings, styling, and content."
              type="info"
              showIcon
              style={{ marginBottom: 24, borderRadius: 8 }}
              action={
                <Button
                  size="small"
                  type="link"
                  onClick={() => setTemplateModalVisible(true)}
                >
                  Browse All Templates
                </Button>
              }
            />

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Gamification Type</div>
              <Select
                size="large"
                value={values.gamification?.type}
                onChange={(value) => {
                  handleGamificationChange("type", value);
                  // Initialize with defaults when switching types
                  const normalizedType = normalizeGamificationType(value);
                  const defaults = gamificationDefaults[normalizedType];
                  if (defaults) {
                    if (normalizedType === "spin_wheel" && wheelSegments.length === 0) {
                      setWheelSegments([...defaults.wheelSegments]);
                    } else if (normalizedType === "streak" && streakRewards.length === 0) {
                      setStreakRewards([...defaults.streakRewards]);
                      handleGamificationChange("streakDays", defaults.streakDays);
                    } else if (normalizedType === "progress_bar" && stepLabels.length === 0) {
                      setStepLabels([...defaults.stepLabels]);
                      handleGamificationChange("progressSteps", defaults.progressSteps);
                    }
                  }
                }}
                style={{ width: "100%", borderRadius: 8 }}
                optionLabelProp="label"
              >
                {gamificationTypes.map((type) => (
                  <Option key={type.value} value={type.value} label={`${type.icon} ${type.label}`}>
                    <Space>
                      <span style={{ fontSize: 18 }}>{type.icon}</span>
                      <div>
                        <div style={{ fontWeight: 500 }}>{type.label}</div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>{type.description}</div>
                      </div>
                    </Space>
                  </Option>
                ))}
              </Select>
            </div>

            {/* Render type-specific configuration */}
            {values.gamification?.type && values.gamification?.type !== "none" && renderGamificationConfig()}

            {/* Validation Errors Summary */}
            {Object.keys(validationErrors).filter(k => k.includes("wheel") || k.includes("slot") || k.includes("streak")).length > 0 && (
              <Alert
                message="Configuration Issues"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {Object.entries(validationErrors)
                      .filter(([k]) => k.includes("wheel") || k.includes("slot") || k.includes("streak"))
                      .map(([key, message]) => (
                        <li key={key}>{message}</li>
                      ))}
                  </ul>
                }
                type="error"
                showIcon
                style={{ marginTop: 16, borderRadius: 8 }}
              />
            )}

            {/* Template Selection Modal */}
            <Modal
              title="Choose a Template"
              open={templateModalVisible}
              onCancel={() => setTemplateModalVisible(false)}
              footer={null}
              width={700}
            >
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">
                  Select a template to quickly configure your gamification with pre-optimized settings.
                </Text>
              </div>
              <Row gutter={[16, 16]}>
                {gamificationTemplates.map((template) => (
                  <Col span={12} key={template.id}>
                    <div
                      style={styles.templateCard}
                      onClick={() => applyTemplate(template)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#8B5CF6";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(139, 92, 246, 0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e5e7eb";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <Space align="start">
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: 12,
                          background: `linear-gradient(135deg, ${template.styling.primaryColor}20, ${template.styling.secondaryColor}20)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 24,
                        }}>
                          {template.icon}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                            {template.name}
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
                            {template.description}
                          </div>
                          <Tag color="purple">
                            {gamificationTypes.find(t => normalizeGamificationType(t.value) === template.gamificationType)?.label}
                          </Tag>
                        </div>
                      </Space>
                    </div>
                  </Col>
                ))}
              </Row>
            </Modal>
          </>
        );

      case "content":
        return (
          <>
            <div style={styles.sectionTitle}>Promotion Content</div>
            <div style={styles.sectionDescription}>
              Write compelling copy for your promotion
            </div>

            <div style={styles.formGroup}>
              <Space size={8}>
                {["en", "es", "fr"].map((locale) => (
                  <Tag
                    key={locale}
                    color={activeLocale === locale ? "blue" : "default"}
                    onClick={() => {
                      if (!values.content?.[locale] && locale !== "en") {
                        setValues((prev) => ({
                          ...prev,
                          content: { ...prev.content, [locale]: { ...initialState.content.en } },
                        }));
                      }
                      setActiveLocale(locale);
                    }}
                    style={{ cursor: "pointer", padding: "4px 12px", borderRadius: 6 }}
                  >
                    {locale === "en" ? "English" : locale === "es" ? "Spanish" : "French"}
                  </Tag>
                ))}
              </Space>
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Badge Text</div>
              <Input
                size="large"
                placeholder="e.g., LIMITED TIME"
                value={values.content?.[activeLocale]?.badge || ""}
                onChange={(e) => handleContentChange(activeLocale, "badge", e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Headline</div>
              <Input
                size="large"
                placeholder="e.g., Black Friday Sale - 50% Off!"
                value={values.content?.[activeLocale]?.headline || ""}
                onChange={(e) => handleContentChange(activeLocale, "headline", e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Subheadline</div>
              <TextArea
                rows={3}
                placeholder="e.g., Limited time offer. Upgrade your plan today and save big!"
                value={values.content?.[activeLocale]?.subheadline || ""}
                onChange={(e) => handleContentChange(activeLocale, "subheadline", e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Primary CTA</div>
                  <Input
                    size="large"
                    placeholder="e.g., Claim Offer"
                    value={values.content?.[activeLocale]?.ctaText || ""}
                    onChange={(e) => handleContentChange(activeLocale, "ctaText", e.target.value)}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Secondary CTA</div>
                  <Input
                    size="large"
                    placeholder="e.g., Learn More"
                    value={values.content?.[activeLocale]?.ctaSecondaryText || ""}
                    onChange={(e) => handleContentChange(activeLocale, "ctaSecondaryText", e.target.value)}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              </Col>
            </Row>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Terms & Conditions</div>
              <TextArea
                rows={2}
                placeholder="e.g., *Offer valid until Dec 31. Cannot be combined with other offers."
                value={values.content?.[activeLocale]?.terms || ""}
                onChange={(e) => handleContentChange(activeLocale, "terms", e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // Preview rendering helper
  const renderGamificationPreview = () => {
    const gamificationType = normalizeGamificationType(values.gamification?.type);

    switch (gamificationType) {
      case "spin_wheel":
        if (wheelSegments.length === 0) return null;
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              width: 140,
              height: 140,
              borderRadius: "50%",
              margin: "0 auto 12px",
              position: "relative",
              background: `conic-gradient(${wheelSegments.map((seg, i) => {
                const startAngle = wheelSegments.slice(0, i).reduce((acc, s) => acc + (s.probability || 0), 0) * 3.6;
                const endAngle = startAngle + (seg.probability || 0) * 3.6;
                return `${seg.color || segmentColors[i % segmentColors.length]} ${startAngle}deg ${endAngle}deg`;
              }).join(", ")})`,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              border: "4px solid #fff",
            }}>
              <div style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
              }}>
                🎡
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              {wheelSegments.length} segments
            </div>
          </div>
        );

      case "scratch_card":
        return (
          <div style={{
            width: 120,
            height: 80,
            margin: "0 auto 16px",
            background: "linear-gradient(135deg, #9ca3af, #6b7280)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            flexDirection: "column",
          }}>
            <span style={{ fontSize: 20 }}>🎴</span>
            <span style={{ marginTop: 4 }}>Scratch {values.gamification?.scratchThreshold || 50}%</span>
          </div>
        );

      case "slot_machine":
        return (
          <div style={{
            width: 160,
            height: 60,
            margin: "0 auto 16px",
            background: "linear-gradient(135deg, #f59e0b, #d97706)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            border: "3px solid #fbbf24",
          }}>
            {(values.gamification?.slotSymbols || ["🍒", "7️⃣", "💎"]).slice(0, 3).map((emoji, i) => (
              <div key={i} style={{
                width: 36,
                height: 40,
                background: "#fff",
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)",
              }}>
                {emoji}
              </div>
            ))}
          </div>
        );

      case "countdown":
        return (
          <div style={{
            margin: "0 auto 16px",
            padding: "12px 16px",
            background: "linear-gradient(135deg, #EF4444, #DC2626)",
            borderRadius: 8,
            color: "#fff",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 10, marginBottom: 4 }}>{values.gamification?.urgencyMessage || "Hurry!"}</div>
            <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
              {["00", "12", "34", "56"].map((val, i) => (
                <div key={i} style={{
                  background: "rgba(0,0,0,0.2)",
                  padding: "4px 8px",
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 700,
                }}>
                  {val}
                </div>
              ))}
            </div>
          </div>
        );

      case "progress_bar":
        return (
          <div style={{ margin: "0 auto 16px", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 4 }}>
              {Array.from({ length: values.gamification?.progressSteps || 5 }).map((_, i) => (
                <div key={i} style={{
                  flex: 1,
                  height: 8,
                  background: i < (values.gamification?.currentStep || 1) ? "#10B981" : "#e5e7eb",
                  borderRadius: i === 0 ? "4px 0 0 4px" : i === (values.gamification?.progressSteps || 5) - 1 ? "0 4px 4px 0" : 0,
                }} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", textAlign: "center" }}>
              Step {values.gamification?.currentStep || 1} of {values.gamification?.progressSteps || 5}
            </div>
          </div>
        );

      case "streak":
        return (
          <div style={{ margin: "0 auto 16px", textAlign: "center" }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>🔥</div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{values.gamification?.streakDays || 7} Day Streak</div>
            <div style={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 8 }}>
              {Array.from({ length: Math.min(values.gamification?.streakDays || 7, 7) }).map((_, i) => (
                <div key={i} style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: i < 3 ? "#F59E0B" : "#e5e7eb",
                }} />
              ))}
            </div>
          </div>
        );

      case "quiz":
        return (
          <div style={{
            width: 160,
            margin: "0 auto 16px",
            padding: 12,
            background: "linear-gradient(135deg, #8b5cf6, #6366f1)",
            borderRadius: 8,
            textAlign: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>❓</div>
            <div style={{ color: "#fff", fontSize: 11, fontWeight: 500 }}>
              Answer to win!
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.pageContainer}>
      {/* Header */}
      <div style={styles.header}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => history.goBack()}
            style={{ borderRadius: 8 }}
          >
            Back
          </Button>
          <Title level={3} style={{ margin: 0 }}>Edit Promotion</Title>
        </Space>
      </div>

      {/* Info Banner */}
      <div style={styles.infoBanner}>
        <div style={styles.infoBannerPattern} />
        <Row align="middle" justify="space-between" style={{ position: "relative", zIndex: 1 }}>
          <Col>
            <Space direction="vertical" size={4}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <EditOutlined style={{ fontSize: 24 }} />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>
                    {values.name || "Untitled Promotion"}
                  </div>
                  <div style={{ fontSize: 14, opacity: 0.9 }}>
                    <Space>
                      <Badge status={values.isActive ? "success" : "default"} />
                      {values.isActive ? "Active" : "Inactive"}
                      <span>-</span>
                      <span>/{values.slug || "no-slug"}</span>
                    </Space>
                  </div>
                </div>
              </div>
            </Space>
          </Col>
          <Col>
            <Tag style={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "#fff",
              padding: "4px 12px",
              borderRadius: 6,
            }}>
              {promotionTypes.find(t => t.value === values.type)?.icon} {promotionTypes.find(t => t.value === values.type)?.label}
            </Tag>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Row gutter={24}>
        <Col span={17}>
          <Card style={styles.mainCard} bodyStyle={{ padding: 0 }}>
            <Row>
              {/* Section Navigation */}
              <Col flex="220px">
                <div style={styles.sectionNav}>
                  {sections.map((section) => (
                    <div
                      key={section.key}
                      style={styles.navItem(activeSection === section.key)}
                      onClick={() => setActiveSection(section.key)}
                    >
                      <div style={styles.navIcon(activeSection === section.key, section.color)}>
                        {section.icon}
                      </div>
                      <div>
                        <div style={{
                          fontWeight: activeSection === section.key ? 600 : 400,
                          color: activeSection === section.key ? "#111827" : "#6b7280",
                          fontSize: 14,
                        }}>
                          {section.label}
                        </div>
                      </div>
                      {activeSection === section.key && (
                        <CheckCircleOutlined style={{ marginLeft: "auto", color: section.color }} />
                      )}
                    </div>
                  ))}
                </div>
              </Col>

              {/* Section Content */}
              <Col flex="1">
                <div style={styles.sectionContent}>
                  <Form layout="vertical">
                    {renderSectionContent()}
                  </Form>
                </div>
              </Col>
            </Row>

            {/* Footer */}
            <div style={styles.footer}>
              <Text type="secondary">
                Editing: {values.name || "Untitled Promotion"}
              </Text>
              <Space>
                <Button size="large" onClick={() => history.goBack()} style={{ borderRadius: 8 }}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="large"
                  loading={saving}
                  onClick={handleSubmit}
                  style={{ borderRadius: 8, height: 44, padding: "0 32px" }}
                >
                  <Space size={8}>
                    <SaveOutlined />
                    <span>Update Promotion</span>
                  </Space>
                </Button>
              </Space>
            </div>
          </Card>
        </Col>

        {/* Preview Panel */}
        <Col span={7}>
          <Card style={styles.previewCard} bodyStyle={{ padding: 0 }}>
            <div style={styles.previewHeader}>
              <Space>
                <EyeOutlined />
                <Text strong>Live Preview</Text>
              </Space>
              <Tag color={values.isActive ? "success" : "default"}>
                {values.isActive ? "Active" : "Inactive"}
              </Tag>
            </div>
            <div style={styles.previewContent}>
              <div style={styles.promotionPreview(values.styling?.primaryColor || "#8B5CF6", values.styling?.secondaryColor || "#06B6D4")}>
                {values.content?.en?.badge && (
                  <Tag
                    style={{
                      background: values.styling?.primaryColor || "#8B5CF6",
                      color: "#fff",
                      border: "none",
                      marginBottom: 12,
                      padding: "4px 12px",
                      borderRadius: 4,
                    }}
                  >
                    {values.content.en.badge}
                  </Tag>
                )}
                <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: "#111827" }}>
                  {values.content?.en?.headline || "Your Headline Here"}
                </div>
                <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 16 }}>
                  {values.content?.en?.subheadline || "Your subheadline will appear here"}
                </div>

                {/* Gamification Preview */}
                {renderGamificationPreview()}

                {values.discount?.discountValue > 0 && (
                  <div style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: values.styling?.primaryColor || "#8B5CF6",
                    marginBottom: 16,
                  }}>
                    {values.discount?.currency === "credits" ? (
                      <>+{values.discount.discountValue} Credits</>
                    ) : values.discount?.discountType === "percentage" ? (
                      <>{values.discount.discountValue}% OFF</>
                    ) : (
                      <>{values.discount?.currency === "USD" ? "$" : values.discount?.currency === "EUR" ? "€" : values.discount?.currency === "GBP" ? "£" : ""}{values.discount.discountValue} OFF</>
                    )}
                  </div>
                )}
                <Space>
                  <Button
                    type="primary"
                    style={{
                      background: values.styling?.primaryColor || "#8B5CF6",
                      borderColor: values.styling?.primaryColor || "#8B5CF6",
                      borderRadius: 6,
                    }}
                  >
                    {values.content?.en?.ctaText || "CTA Button"}
                  </Button>
                  {values.content?.en?.ctaSecondaryText && (
                    <Button style={{ borderRadius: 6 }}>
                      {values.content.en.ctaSecondaryText}
                    </Button>
                  )}
                </Space>
                {values.content?.en?.terms && (
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 16 }}>
                    {values.content.en.terms}
                  </div>
                )}
              </div>
            </div>

            {/* Preview Info */}
            <div style={{ padding: 16, borderTop: "1px solid #e5e7eb" }}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Type:</Text>
                  <Text>{promotionTypes.find(t => t.value === values.type)?.label}</Text>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Style:</Text>
                  <Text>{displayStyles.find(s => s.value === values.displayStyle)?.label}</Text>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Priority:</Text>
                  <Text>{values.priority}</Text>
                </div>
                {values.discount?.discountValue > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text type="secondary">
                      {values.discount?.currency === "credits" ? "Reward:" : "Discount:"}
                    </Text>
                    <Tag color={values.discount?.currency === "credits" ? "gold" : "green"}>
                      {values.discount?.currency === "credits"
                        ? `+${values.discount.discountValue} ⭐`
                        : values.discount?.discountType === "percentage"
                        ? `${values.discount.discountValue}%`
                        : `${values.discount?.currency} ${values.discount.discountValue}`}
                    </Tag>
                  </div>
                )}
                {values.gamification?.type !== "none" && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text type="secondary">Gamification:</Text>
                    <Tag color="purple">{gamificationTypes.find(g => g.value === values.gamification?.type)?.label}</Tag>
                  </div>
                )}
              </Space>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default withRouter(PromotionUpdateForm);
